<template>
    <div class="column q-col-gutter-md q-pa-md">
        <div class="col-sm-12">
            <QSelect
                v-model="datasetsNameSelected"
                :options="datasetsName"
                :label="$t('visualization.contextComparison.datasets')"
                :dense="true"
                @update:modelValue="onDatasetSelect"
            />
        </div>
        <div class="col-sm-12">
            <QSelect
                v-model="setSelected"
                dense
                :options="setsName"
                :disable=" setsName.length === 0"
                :label="$t('visualization.contextComparison.filtersets')"
                @update:modelValue="onFilterSetSelect"
            >
                <template #append>
                    <q-icon
                        v-if="showClearIcon"
                        class="cursor-pointer"
                        name="clear"
                        @click="onFilterSetSelect(null)"
                    />
                </template>
            </QSelect>
            <QTooltip v-if="setsName.length === 0">
                {{ $t('visualization.contextComparison.noFiltersets') }}
            </QTooltip>
        </div>
        <div class="col-sm-12">
            <div class="row q-col-gutter-sm">
                <div class="col-sm-6">
                    <span class="ContextualElementName">{{ $t(`visualization.contextComparison.cases`) }}</span>
                    <QCard
                        flat
                        class="bg-grey-2 metric"
                    >
                        <QCardSection
                            horizontal
                            class="text-weight-bold"
                        >
                            {{ cases !== '-' ? formatNumber(cases) : cases }}
                        </QCardSection>
                        <QCardSection
                            v-if="casesDifference.number !== null"
                            horizontal
                            class="q-pa-sm q-ml-sm bg-white row items-center"
                        >
                            <QIcon
                                :name="casesDifference.direction"
                                color="primary"
                                class="metricIcon"
                            />
                            <span class="text-weight-bold">
                                {{ formatNumber(casesDifference.number) }}
                            </span>
                        </QCardSection>
                    </QCard>
                </div>
                <div class="col-sm-6">
                    <span class="ContextualElementName">{{ $t(`visualization.contextComparison.variants`) }}</span>
                    <QCard
                        flat
                        class="bg-grey-2 metric"
                    >
                        <QCardSection
                            horizontal
                            class="text-weight-bold"
                        >
                            {{ variants !== '-' ? formatNumber(variants) : variants }}
                        </QCardSection>
                        <QCardSection
                            v-if="variantsDifference.number !== null"
                            horizontal
                            class="q-pa-sm q-ml-sm bg-white row items-center"
                        >
                            <QIcon
                                :name="variantsDifference.direction"
                                color="primary"
                                class="metricIcon"
                            />
                            <span class="text-weight-bold">
                                {{ formatNumber(variantsDifference.number) }}
                            </span>
                        </QCardSection>
                    </QCard>
                </div>
            </div>
        </div>
        <div class="col-sm-12">
            <span class="ContextualElementName">{{ $t(`visualization.contextComparison.duration`) }}</span>
            <div class="row q-col-gutter-sm items-center">
                <div class="col-sm-8">
                    <QCard
                        flat
                        class="bg-grey-2 metricDuration"
                    >
                        <QCardSection
                            horizontal
                            class="text-weight-bold"
                        >
                            {{ duration !== '-' ? formatDuration(duration) : duration }}
                        </QCardSection>
                        <QCardSection
                            v-if="durationDifference.metric"
                            horizontal
                            class="q-pa-sm q-ml-sm bg-white row items-center"
                        >
                            <QIcon
                                :name="durationDifference.direction"
                                :color="getColorClass(durationDifference.direction)"
                                class="metricIcon"
                            />
                            <span class="text-weight-bold">
                                {{ formatDuration(durationDifference.metric) }}
                            </span>
                        </QCardSection>
                    </QCard>
                </div>
                <div class="col-sm-4 text-center">
                    <QFab
                        v-model="displayDurationOptions"
                        :label="metricsDurationSelected.value"
                        square
                        flat
                        labelPosition="left"
                        color="grey-2"
                        textColor="primary"
                        icon="keyboard_arrow_down"
                        direction="up"
                        padding="sm"
                    >
                        <QFabAction
                            v-for="option in metricsDuration"
                            :key="option.value"
                            textColor="primary"
                            color="grey-2"
                            :label="option.label"
                            padding="xs"
                            @click="refreshDuration(option)"
                        />
                    </QFab>
                </div>
            </div>
        </div>
        <div class="col-sm-12">
            <div class="row q-col-gutter-sm">
                <div class="col-sm-6">
                    <div>
                        <span class="ContextualElementName">{{ $t(`visualization.contextComparison.start`) }}</span>
                        <span class="dateDifference">{{ startDateDifference }}</span>
                    </div>
                    <QCard
                        flat
                        class="bg-grey-2 metric"
                    >
                        <div class="q-pa-sm text-center text-weight-bold">
                            {{ startDate !== '-' ? App.dateLocationFormat(startDate) : startDate }}
                        </div>
                    </QCard>
                </div>
                <div class="col-sm-6">
                    <div>
                        <span class="ContextualElementName">{{ $t(`visualization.contextComparison.end`) }}</span>
                        <span class="dateDifference">{{ endDateDifference }}</span>
                    </div>
                    <QCard
                        flat
                        class="bg-grey-2 metric"
                    >
                        <div class="q-pa-sm text-center text-weight-bold">
                            {{ endDate !== '-' ? App.dateLocationFormat(endDate) : endDate }}
                        </div>
                    </QCard>
                </div>
            </div>
        </div>
        <q-inner-loading :showing="loading" />
    </div>
</template>

<script>
import VueTypes from 'vue-types'
import moment from 'moment'
import 'moment-duration-format'
import { filtersStorageMixin } from '@/mixins'
import {
    Api, apiRequest, notifyError,
} from '@/api'

const emptyId = '00000000-0000-0000-0000-000000000000'

export default {
    name: 'ContextComparisonCard',
    components: {
    },
    mixins: [filtersStorageMixin],
    inject: ['App'],
    props: {
        filteredInfo: VueTypes.oneOfType([VueTypes.object, VueTypes.array]),
        sectionAssociated: VueTypes.object,
        processId: VueTypes.oneOfType([VueTypes.string, VueTypes.number]),
        datasets: VueTypes.array.def([]),
    },
    emits: ['filterSelected', 'csvPosition'],
    data() {
        return {
            displayDurationOptions: false,
            loading: false,
            sets: [],
            setsName: [],
            setSelected: null,
            currentDataset: null,
            datasetsNameSelected: null,
            selectedInfoFiltered: null,
            datasetSelected: null,
            filterSelected: null,
            datasetSelectedUuid: null,
            metricsDuration: [
                { value: 'med', label: this.$t('visualization.filters.contextualInfo.med') },
                { value: 'avg', label: this.$t('visualization.filters.contextualInfo.avg') },
                { value: 'sd', label: this.$t('visualization.filters.contextualInfo.sd') },
                { value: 'min', label: this.$t('visualization.filters.contextualInfo.min') },
                { value: 'max', label: this.$t('visualization.filters.contextualInfo.max') },
            ],
            metricsDurationSelected: {
                label: this.$t('visualization.filters.contextualInfo.avg'),
                value: 'avg',
            },
            cases: '-',
            variants: '-',
            duration: '-',
            endDate: '-',
            startDate: '-',
            variantsDifference: {
                number: null,
                direction: null,
            },
            casesDifference: {
                number: null,
                direction: null,
            },
            durationDifference: {
                metric: null,
                direction: null,
            },
            startDateDifference: '',
            endDateDifference: '',
            noSets: null,
            csvPosition: {},
            currentComparison: null,
        }
    },
    computed: {
        showClearIcon() {
            return this.setSelected && this.setsName.length > 0
        },
        datasetsName() {
            if (this.datasets && this.datasets.length > 0) {
                return this.datasets.map((item) => item.name)
            }
            return []
        },
    },
    mounted() {
        this.currentComparison = this.sectionAssociated
        if (this.sectionAssociated.comparedDatasetId) {
            this.datasetsNameSelected = this.datasets.find((item) => item.uuid === this.currentComparison.comparedDatasetId)?.name || null
            this.datasetFiltersCall()
        }
    },
    methods: {
        onDatasetSelect(val) {
            this.datasetsNameSelected = val
            this.datasetFiltersCall()
            this.emitData()
        },
        onFilterSetSelect(val) {
            this.setSelected = val
            this.getModelInfoFiltered()
            this.emitData()
        },
        datasetFiltersCall() {
            this.loading = true
            this.setSelected = ''
            this.datasetSelected = this.datasets.find((item) => item.name === this.datasetsNameSelected)
            const uuidSelected = this.datasetSelected.uuid
            apiRequest(Api().datasets.getDatasetFilters({ datasetId: uuidSelected }))
                .then(({ data }) => {
                    this.sets = data
                    this.noSets = data.length === 0 ? 'noSets' : null
                    this.setsName = data.map((s) => s.name)
                    const validFilterId = this.currentComparison.filtersetId
                                          && this.currentComparison.filtersetId !== emptyId
                    if (validFilterId) {
                        const match = data.find((s) => s.uuid === this.currentComparison.filtersetId)
                        if (match) this.setSelected = match.name
                    }
                })
                .catch(notifyError)
                .finally(() => {
                    this.getModelInfoFiltered()
                })
        },
        getModelInfoFiltered() {
            this.loading = true
            const processid = this.datasets.find((item) => item.name === this.datasetsNameSelected).uuid
            const selectedSet = this.sets.find((s) => s.name === this.setSelected)

            this.filterSelected = selectedSet
                ? { name: selectedSet.name, filters: selectedSet.filters, uuid: selectedSet.uuid }
                : { filters: [] }
            apiRequest(Api().visualizations.modelInfo({ processId: processid, params: this.filterSelected }))
                .then((data) => {
                    this.selectedInfoFiltered = data
                    this.getContext()
                })
                .catch(notifyError)
                .finally(() => {
                    this.loading = false
                })
        },
        emitData() {
            const dataSelected = {
                sectionId: this.currentComparison.sectionId,
                comparedDatasetId: this.datasetSelected?.uuid || emptyId,
                filtersetId: this.filterSelected?.uuid || emptyId,
            }
            this.$emit('filterSelected', dataSelected)
        },
        getContext() {
            if (this.currentComparison.metricDuration) {
                this.metricsDurationSelected = this.currentComparison.metricDuration
            } else {
                this.metricsDurationSelected = { value: 'avg', label: this.$t('visualization.filters.contextualInfo.avg') }
            }
            const formatDuration = this.metricsDurationSelected.value
            this.variants = this.selectedInfoFiltered.variants
            this.cases = this.selectedInfoFiltered.cases
            this.duration = this.selectedInfoFiltered.duration[formatDuration]
            this.endDate = this.selectedInfoFiltered.maxEndDateTime
            // eslint-disable-next-line max-len
            this.startDate = this.selectedInfoFiltered.minStartDateTime
            this.compareMetrics(formatDuration)
        },
        compareDirection(value) {
            let icon = ''
            if (value < 0) {
                icon = 'arrow_upward'
            } else if (value > 0) {
                icon = 'arrow_downward'
            } else {
                icon = 'remove'
            }
            return icon
        },
        getColorClass(icon) {
            const colorMap = {
                arrow_upward: 'red',
                arrow_downward: 'green',
            }
            return colorMap[icon] || 'primary'
        },
        compareMetrics(formatDuration) {
            this.currentDataset = this.datasets.find((item) => item.uuid === this.processId)
            const differenceVariant = this.filteredInfo.variants - this.selectedInfoFiltered.variants
            this.variantsDifference.number = Math.abs(differenceVariant)
            this.variantsDifference.direction = this.compareDirection(differenceVariant)
            const differenceCases = this.filteredInfo.cases - this.selectedInfoFiltered.cases
            this.casesDifference.number = Math.abs(differenceCases)
            this.casesDifference.direction = this.compareDirection(differenceCases)
            const durationVariant = this.filteredInfo.duration[formatDuration] - this.selectedInfoFiltered.duration[formatDuration]
            this.durationDifference.metric = Math.abs(durationVariant)
            this.durationDifference.direction = this.compareDirection(durationVariant)
            this.endDateDifference = this.compareDate(this.filteredInfo.maxEndDateTime, this.selectedInfoFiltered.maxEndDateTime)
            this.startDateDifference = this.compareDate(this.filteredInfo.minStartDateTime, this.selectedInfoFiltered.minStartDateTime)
            this.csvPosition = {
                id: this.currentComparison.sectionId,
                data: {
                    currentDataset: this.currentDataset?.name,
                    datasetComparison: this.datasetSelected.name,
                    filterset: this.setSelected ? this.setSelected : '',
                    currentCases: Math.abs(this.filteredInfo.cases),
                    casesComparison: this.cases,
                    casesDifference: this.casesDifference.number,
                    currentVariants: Math.abs(this.filteredInfo.variants),
                    variantsComparison: this.variants,
                    variantsDifference: this.variantsDifference.number,
                    currentDuration: this.filteredInfo.duration[this.metricsDurationSelected.value],
                    durationComparison: this.duration,
                    durationDifference: this.durationDifference.metric,
                    metric: this.metricsDurationSelected.label,
                    currentStartDate: this.App.dateLocationFormat(this.filteredInfo.minStartDateTime),
                    startDateComparison: this.App.dateLocationFormat(this.selectedInfoFiltered.minStartDateTime),
                    startDateDifference: this.diffDate(this.filteredInfo.minStartDateTime, this.selectedInfoFiltered.minStartDateTime),
                    currentEndDate: this.App.dateLocationFormat(this.filteredInfo.maxEndDateTime),
                    endDateComparison: this.App.dateLocationFormat(this.selectedInfoFiltered.maxEndDateTime),
                    endDateDifference: this.diffDate(this.filteredInfo.maxEndDateTime, this.selectedInfoFiltered.maxEndDateTime),
                },
            }
            this.$emit('csvPosition', this.csvPosition)
        },
        diffDate(referenceDate, comparisonDate) {
            const date1 = moment(referenceDate)
            const date2 = moment(comparisonDate)
            return date2.diff(date1, 'seconds')
        },
        compareDate(referenceDate, comparisonDate) {
            const momentReference = moment.utc(referenceDate)
            const momentComparison = moment.utc(comparisonDate)
            const result = momentReference.diff(momentComparison, 'seconds') > 0
                ? this.$t('visualization.contextComparison.before')
                : this.$t('visualization.contextComparison.after')
            return result
        },
        formatNumber(value) {
            let formattedValue = value
            if (formattedValue >= 1000) {
                const abbreviated = (value / 1000).toFixed(1)
                formattedValue = `${this.App.numberLocationFormat(abbreviated)}K`
            }
            return formattedValue
        },
        formatDuration(currentSeconds) {
            const duration = moment.duration(currentSeconds, 'seconds')
            return duration ? duration.format('d[d] h[h]:m[m]:s[s]', { largest: 2, trim: false }) : '-'
        },
        refreshDuration(metrics) {
            this.metricsDurationSelected = metrics
            this.duration = this.selectedInfoFiltered.duration[metrics.value]
            const durationVariant = this.filteredInfo.duration[metrics.value] - this.selectedInfoFiltered.duration[metrics.value]
            this.durationDifference.metric = Math.abs(durationVariant)
            this.durationDifference.direction = this.compareDirection(durationVariant)
        },
    },
}
</script>
<style scoped lang="scss">

  .metricIconDuration, .metricIcon{
    font-size: 28px;
  }
 .metricIconDurationEqual{
    margin-left:10%;
    margin-right: 10px;
    margin-bottom: 5px;
    font-size: 2em;
    color:$primary;
 }

  .metric, .metricDuration {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 50px;
  }

  .ContextualElementName{
    font-weight: 700;
  }
  .dateDifference{
    color: $primary;
    margin-left: 8px;
    font-size: small;
    font-weight: bold;
    margin-top: 3.8px;
  }
  .metricIcon{
    margin-right: 3px;
  }

</style>
